import { axios, getResponse, isUndNullEmp, settings } from '../../helpers/main';
import { userAuthContextType } from '../../auth/AuthProvider';

const authServices = { user: userAuthContextType }; //eslint-disable-line
const objectService = getResponse({ type: 0 }); //eslint-disable-line

/**
 * Service to manage communication with the Payment Information Details (Accounts) API.
 * Follows the Single Responsibility Principle (SRP) by separating network logic from the UI.
 */
export class PaymentInformationDetailService {
    /**
     * Retrieves a Payment Information Detail by ID.
     * @param {object} params - Parameters.
     * @param {string} params.token - User token.
     * @param {number|string} params.id - Detail ID.
     * @param {number} params.languageId - Language ID.
     * @param {string} [params.subMethod=''] - Sub-method name.
     * @returns {Promise<objectService>} Promise with the response.
     */
    static getById = async ({
        token = '',
        id = '',
        languageId = 1,
        subMethod = '',
        auth = authServices,
    } = {}) => {
        let response = getResponse({ type: 0 });
        const currentMethod = 'getById';
        try {
            const requestObj = {
                url: `${settings?.backend?.urls?.api}${settings?.backend?.urls?.apiV1}${settings?.backend?.urls?.paymentInformationDetails}/`,
                headers: {
                    authorization: `bearer ${token}`,
                },
                data: {
                    id: parseInt(id),
                    method: settings?.backend?.methods?.paymentInformationDetails.getById,
                    languageId: languageId,
                },
                subMethod: subMethod || currentMethod,
            };
            response = await axios.post(requestObj);
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        }
        return response;
    }

    /**
     * Deletes a Payment Information Detail by ID.
     * @param {Object} params - Parameters.
     * @param {string} params.token - User token.
     * @param {number|string} params.id - Detail ID.
     * @param {number} params.languageId - Language ID.
     * @param {string} [params.subMethod=''] - Sub-method name.
     * @returns {Promise<objectService>} Promise with the response.
     */
    static deleteAsync = async ({
        token = '',
        id = '',
        languageId = 1,
        subMethod = '',
        auth = authServices,
    } = {}) => {
        let response = getResponse({ type: 0 });
        const currentMethod = 'deleteAsync';
        try {
            const requestObj = {
                url: `${settings?.backend?.urls?.api}${settings?.backend?.urls?.apiV1}${settings?.backend?.urls?.paymentInformationDetails}/${id}`,
                headers: {
                    authorization: `bearer ${token}`,
                },
                data: {
                    languageId: languageId,
                },
                subMethod: subMethod || currentMethod,
            };
            response = await axios.delete(requestObj);
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        }
        return response;
    }
    /**
     * Creates a new Payment Information Detail.
     * @param {Object} params - Parameters.
     * @param {string} params.token - User token.
     * @param {Object} params.data - Detail data.
     * @param {number} params.languageId - Language ID.
     * @param {string} [params.subMethod=''] - Sub-method name.
     * @returns {Promise<objectService>} Promise with the response.
     */
    static create = async ({
        token = '',
        data = {},
        languageId = 1,
        subMethod = '',
        auth = authServices,
    } = {}) => {
        let response = getResponse({ type: 0 });
        const currentMethod = 'create';
        try {
            const requestObj = {
                url: `${settings?.backend?.urls?.api}${settings?.backend?.urls?.apiV1}${settings?.backend?.urls?.paymentInformationDetails}/`,
                headers: {
                    authorization: `bearer ${token}`,
                },
                data: {
                    data: data,
                    languageId: languageId,
                },
                subMethod: subMethod || currentMethod,
            };
            response = await axios.post(requestObj);
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        }
        return response;
    }

    /**
     * Updates an existing Payment Information Detail.
     * @param {Object} params - Parameters.
     * @param {string} params.token - User token.
     * @param {number|string} params.id - Detail ID.
     * @param {Object} params.data - Detail data.
     * @param {number} params.languageId - Language ID.
     * @param {string} [params.subMethod=''] - Sub-method name.
     * @returns {Promise<objectService>} Promise with the response.
     */
    static update = async ({
        token = '',
        id = '',
        data = {},
        languageId = 1,
        subMethod = '',
        auth = authServices,
    } = {}) => {
        let response = getResponse({ type: 0 });
        const currentMethod = 'update';
        try {
            const requestObj = {
                url: `${settings?.backend?.urls?.api}${settings?.backend?.urls?.apiV1}${settings?.backend?.urls?.paymentInformationDetails}/${id}`,
                headers: {
                    authorization: `bearer ${token}`,
                },
                data: {
                    data: data,
                    languageId: languageId,
                },
                subMethod: subMethod || currentMethod,
            };
            response = await axios.post(requestObj);
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        }
        return response;
    }
}
